import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form as RBForm } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { fetchStates, fetchCities } from '../services/geo.service';
import { submitRequest } from '../services/request.service';

import Button from './Button';
import FormStepper from './FormStepper';

import '../styles/Form.css';


const INITIAL_DATA = {
  firstName: '',
  lastName: '',
  gender: '',
  dni: '',
  phone: '',
  email: '',
  province: '',
  city: ''
};

const STEP_META = [
  {
    headline: 'Completá tus datos personales',
    copy: 'Contanos quién sos para iniciar la evaluación y poder simular tu préstamo.'
  },
  {
    headline: 'Ingresá los datos para que te contactemos',
    copy: 'Usaremos esta información para enviarte las mejores ofertas en minutos.'
  },
  {
    headline: '¿Dónde te encontramos?',
    copy: 'Indicá tu ubicación para validar la disponibilidad de las financieras en tu zona.'
  }
];

const STEP_FIELDS = [
  ['firstName', 'lastName', 'gender', 'dni'],
  ['phone', 'email'],
  ['province', 'city']
];

const COUNTRY_CODE = 'AR';

function Form() {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_DATA);
  const [statesOptions, setStatesOptions] = useState([]);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(true);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let canceled = false;

    const loadStates = async () => {
      setIsLoadingStates(true);
      setError('');

      const { options, error } = await fetchStates(COUNTRY_CODE);
      if (canceled) return;

      const availableStates = ['Buenos Aires', 'Ciudad Autónoma de Buenos Aires'];
      setStatesOptions(
        options.filter((state) => availableStates.includes(state)).sort((a, b) => a.localeCompare(b))
      );
      setError(error || '');
      setIsLoadingStates(false);
    };

    loadStates();
    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    if (!formData.province) {
      setCitiesOptions([]);
      setIsLoadingCities(false);
      return;
    }

    let canceled = false;

    const loadCities = async () => {
      setIsLoadingCities(true);
      setError('');

      const { options, error } = await fetchCities(COUNTRY_CODE, formData.province);
      if (canceled) return;

      setCitiesOptions(options.sort((a, b) => a.localeCompare(b)));
      setError(error || '');
      setIsLoadingCities(false);
    };

    loadCities();
    return () => {
      canceled = true;
    };
  }, [formData.province]);

  const totalSteps = STEP_META.length;

  const isCurrentStepValid = activeStep === 2 
    ? STEP_FIELDS[activeStep].every((field) => formData[field].trim() !== '') && termsAccepted
    : STEP_FIELDS[activeStep].every((field) => formData[field].trim() !== '');

  const handleSubmit = async () => {
    const { success, data, error } = await submitRequest(formData);
    if (success) {
      navigate('/cotizador/' + data.requestId);
    } else if (error) {
      setError(error);
    }
  }

  const validStep = (step) => {
    let valid = false;
    let validEmail = false;
    let validPhone = false;

    switch (step) {
      case 0:
        valid = /^[0-9]{7,11}$/.test(formData.dni.replace(/\./g, ''));
        setError(valid ? '' : 'El DNI ingresado no es válido.');
        return valid;
      case 1:
        validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        validPhone = /^\+?\d{7,15}$/.test(formData.phone.replace(/\s+/g, ''));
        if (!validEmail ) {
        setError('El email ingresado no es válido.');
        return false;
        }
        if (!validPhone){
        setError('El teléfono ingresado no es válido.');
        return false;
        }
        setError('');
        return true;
      case 2:
        return statesOptions.includes(formData.province) &&
          citiesOptions.includes(formData.city);
      default:
        return false;
    }
  };

  const capitalizeWords = (str) => str.replace(/\b\w/g, (char) => char.toUpperCase());

  const handleChange = (event) => {
    const { name, value } = event.target;

    const formatValue = (fieldName, fieldValue) => {
      if (fieldName === 'firstName' || fieldName === 'lastName') {
        return capitalizeWords(fieldValue);
      }
      if (fieldName === 'dni') {
        return fieldValue.replace(/[^0-9]/g, '').slice(0, 11);
      }
      return fieldValue;
    };

    setFormData((prev) => ({
      ...prev,
      [name]: formatValue(name, value),
      ...(name === 'province' ? { city: '' } : {})
    }));
  };

  const handleNext = () => {
    if (activeStep < totalSteps - 1 && isCurrentStepValid) {
      if(validStep(activeStep)) {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handlePrev = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Row className="gy-3">
            <Col xs={12}>
              <RBForm.Group controlId="firstName">
                <RBForm.Label>Nombres</RBForm.Label>
                <RBForm.Control
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Ej: Juan José"
                  required
                />
              </RBForm.Group>
            </Col>
            <Col xs={12}>
              <RBForm.Group controlId="lastName">
                <RBForm.Label>Apellidos</RBForm.Label>
                <RBForm.Control
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Ej: Pérez"
                  required
                />
              </RBForm.Group>
            </Col>
            <Col xs={12}>
              <RBForm.Group controlId="dni">
                <RBForm.Label>DNI</RBForm.Label>
                <RBForm.Control
                  name="dni"
                  type="number"
                  value={formData.dni}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Ej: 30123456"
                  required
                />
              </RBForm.Group>
            </Col>
            <Col xs={12}>
              <RBForm.Group controlId="gender">
                <RBForm.Label>Género</RBForm.Label>
                <RBForm.Select name="gender" value={formData.gender} onChange={handleChange} required>
                  <option value="">Seleccioná una opción</option>
                  <option value="F">Femenino</option>
                  <option value="M">Masculino</option>
                </RBForm.Select>
              </RBForm.Group>
            </Col>
          </Row>
        );
      case 1:
        return (
          <Row className="gy-3">
            <Col xs={12}>
              <RBForm.Group controlId="phone">
                <RBForm.Label>Teléfono</RBForm.Label>
                <RBForm.Control
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Ej: +54 9 11 1234 5678"
                  required
                />
              </RBForm.Group>
            </Col>
            <Col xs={12}>
              <RBForm.Group controlId="email">
                <RBForm.Label>Email</RBForm.Label>
                <RBForm.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tuemail@ejemplo.com"
                  required
                />
              </RBForm.Group>
            </Col>
          </Row>
        );
      case 2:
        return (
          <Row className="gy-3">
            <Col xs={12}>
              <RBForm.Group controlId="province">
                <RBForm.Label>Provincia</RBForm.Label>
                <RBForm.Select
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  disabled={isLoadingStates || statesOptions.length === 0}
                  required
                >
                  <option value="">
                    {isLoadingStates ? 'Cargando provincias...' : 'Seleccioná una provincia'}
                  </option>
                  {statesOptions.map((province) => (
                    <option key={province} value={province}>
                      {province}
                    </option>
                  ))}
                </RBForm.Select>
              </RBForm.Group>
            </Col>
            <Col xs={12}>
              <RBForm.Group controlId="city">
                <RBForm.Label>Ciudad</RBForm.Label>
                <RBForm.Select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={!formData.province || isLoadingCities || citiesOptions.length === 0}
                  required
                >
                  <option value="">
                    {!formData.province
                      ? 'Seleccioná una provincia primero'
                      : isLoadingCities
                        ? 'Cargando ciudades...'
                        : 'Seleccioná una ciudad'}
                  </option>
                  {citiesOptions.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </RBForm.Select>
              </RBForm.Group>
            </Col>
            <Col xs={12}>
              <RBForm.Group controlId="termsAccepted" className="mt-3">
                <RBForm.Check
                  type="checkbox"
                  name="termsAccepted"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  label="Acepto los términos y condiciones"
                  required
                />
              </RBForm.Group>
            </Col>
            <Col xs={12}>
              <div className="disclaimer-box">
                <h4 className="disclaimer-title">¡ATENCIÓN!</h4>
                <p className="disclaimer-text">
                  Sólo realizamos créditos personales a personas físicas con dirección dentro de Gran Buenos Aires y Capital Federal. No realizamos envíos al interior del país.
                </p>
              </div>
            </Col>
          </Row>
        );
      default:
        return null;
    }
  };

  const handleKeyDown = (event) => {
    if (event.target.name === 'dni' && (event.key === '.' || event.key === ',')) {
      event.preventDefault(); // Block dots and commas at the keyboard level
    }
  };

  return (
    <div className="quote-form">
      <h2 className="quote-form__title text-center">Cotizar</h2>
      <FormStepper steps={STEP_META} activeStep={activeStep} />
      <Card className="quote-form__card border-0 shadow-sm">
        <Card.Body className="p-3 p-md-5">
          <div className="quote-form__card-header mb-4">
            <h3 className="quote-form__card-title">{STEP_META[activeStep].headline}</h3>
            <p className="quote-form__card-subtitle">
              {STEP_META[activeStep].copy}
            </p>
          </div>
          <RBForm >
            {renderStepContent()}
            {error && (
              <Col xs={12}>
                <div className="text-danger small">{error}</div>
              </Col>
            )}
            <div className="d-flex justify-content-between align-items-center mt-4">
              <Button
                variant="outline-secondary"
                disabled={activeStep === 0}
                onClick={handlePrev}
              >
                Volver
              </Button>
              {activeStep < totalSteps - 1 ? (
                <Button variant="primary" onClick={handleNext} disabled={!isCurrentStepValid}>
                  Siguiente
                </Button>
              ) : (
                <Button onClick={handleSubmit} variant="primary" disabled={!isCurrentStepValid}>
                  Calcular 
                </Button>
              )}
            </div>
          </RBForm>
        </Card.Body>
      </Card>
    </div>
  );
}

export default Form;
